import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import Avatar from "../components/ui/Avatar";
import { motion } from 'framer-motion';
import { scrollToSection } from '../utils/scroll';
import MatrixBackground from '../components/ui/MatrixBackground';

// Section Components
import Hero from '../components/sections/Hero';
import WhyDataEngineering from '../components/sections/WhyDataEngineering';
import AboutMe from '../components/sections/AboutMe';
import Testimonials from '../components/sections/Testimonials';
import Events from '../components/sections/Events';
import Services from '../components/sections/Services';
import PersonalDevelopment from '../components/sections/PersonalDevelopment';
import LoginSection from '../components/sections/LoginSection';
import Contact from '../components/sections/Contact';
import Footer from '../components/sections/Footer';

interface PruebaMatrixProps {
  user?: any;
}

export default function PruebaMatrix({ user }: PruebaMatrixProps) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleCalendlyRedirect = () => {
    window.open('https://calendly.com/lucianoagustinargolo/sesion_informativa', '_blank');
  };

  return (
    <div className="min-h-screen bg-black font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-green-500/30 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xl font-bold text-green-400"
            >
              <Link to="/">Luciano Argolo</Link>
            </motion.div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('sobre-mi')} className="text-green-400 hover:text-green-300 transition-colors">
                Sobre mí
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-green-400 hover:text-green-300 transition-colors">
                Contacto
              </button>
              <button onClick={() => scrollToSection('desarrollo-personal')} className="text-green-400 hover:text-green-300 transition-colors">
                Desarrollo Personal
              </button>
              <div className="flex items-center gap-4">
                {user ? (
                  <Link to="/cursos" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <Avatar user={user} size="sm" />
                  </Link>
                ) : (
                  <Link to="/login" className="text-green-400 hover:text-green-300 transition-colors">
                    Iniciar Sesión
                  </Link>
                )}
                <Button size="sm" onClick={() => scrollToSection('servicios')} className="bg-green-600 hover:bg-green-700 text-white rounded transition-all duration-300 transform hover:scale-105">
                  Mis Servicios
                </Button>
                <Link to="/empresas" className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm rounded transition-all duration-300 transform hover:scale-105">
                  Empresas
                </Link>
              </div>
            </div>
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-green-400 hover:text-green-300 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="md:hidden mt-4 pb-4 border-t border-green-500/30">
              <div className="flex flex-col space-y-3 pt-4">
                <button onClick={() => { scrollToSection('sobre-mi'); setMobileMenuOpen(false); }} className="text-green-400 hover:text-green-300 transition-colors text-left">Sobre mí</button>
                <button onClick={() => { scrollToSection('servicios'); setMobileMenuOpen(false); }} className="text-green-400 hover:text-green-300 transition-colors text-left">Servicios</button>
                <button onClick={() => { scrollToSection('contacto'); setMobileMenuOpen(false); }} className="text-green-400 hover:text-green-300 transition-colors text-left">Contacto</button>
                <button onClick={() => { scrollToSection('desarrollo-personal'); setMobileMenuOpen(false); }} className="text-green-400 hover:text-green-300 transition-colors text-left">Desarrollo Personal</button>
                <Link to="/empresas" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Empresas</Link>
                {!user && (
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Iniciar Sesión</Link>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section - Verde Matrix */}
      <section className="relative pt-32 pb-24 px-6 min-h-screen">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <Hero
            onScrollToSection={scrollToSection}
          />
        </div>
      </section>

      {/* WhyDataEngineering Section - Verde Matrix */}
      <section className="relative py-24">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <WhyDataEngineering />
        </div>
      </section>

      {/* AboutMe Section - Verde Matrix */}
      <section className="relative py-24">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <AboutMe />
        </div>
      </section>

      {/* Testimonials Section - Verde Matrix */}
      <section className="relative py-24">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <Testimonials />
        </div>
      </section>

      {/* Services Section - Verde Matrix */}
      <section className="relative py-24">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <Services 
            onCalendlyRedirect={handleCalendlyRedirect} 
          />
        </div>
      </section>

      {/* LoginSection - Verde Matrix */}
      <section className="relative py-20">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <LoginSection user={user} />
        </div>
      </section>

      {/* PersonalDevelopment Section - Azul Matrix */}
      <section className="relative py-20">
        <MatrixBackground color="#3b82f6" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <PersonalDevelopment user={user} />
        </div>
      </section>

      {/* Events Section - Verde Matrix */}
      <section className="relative py-24">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <Events />
        </div>
      </section>

      {/* Contact Section - Verde Matrix */}
      <section className="relative py-24">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <Contact />
        </div>
      </section>

      {/* Footer Section - Verde Matrix */}
      <section className="relative">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/60 z-[1]"></div>
        <div className="relative z-10">
          <Footer />
        </div>
      </section>
    </div>
  );
}
